/**
 * CSV download helper that fetches the file with the user's auth cookie
 * and triggers a save dialog without opening a blank tab.
 *
 * Why not `window.open`?
 *   - The backend responds with `Content-Disposition: attachment`, which
 *     makes Chrome/Firefox start a download and leave the new tab blank.
 *     Users perceive that as "nothing happened".
 *   - When the response is 401/403/500, `window.open` shows the JSON
 *     error inline in the new tab, which looks identical to "blank".
 *
 * This helper:
 *   1. Issues a credentialed GET via fetch (cookies travel automatically).
 *   2. On non-2xx, throws so the caller can `toast.error(...)`.
 *   3. Reads the body as a Blob, creates a temporary <a download> link,
 *      clicks it, then revokes the object URL.
 *   4. Returns a small summary `{ rows, bytes }` so the caller can warn
 *      when the export is empty (header-only CSV).
 */
import axios from "axios";

export async function downloadCsv(url, filename) {
    const res = await axios.get(url, {
        responseType: "blob",
        // axios.defaults.withCredentials is set globally in /app/frontend/src/lib/auth.js
    });

    const blob = res.data;
    const text = await blob.text();
    // CSV always emits the 4-column header on line 1; data rows start at line 2.
    const lineCount = text.split("\n").filter((l) => l.trim().length > 0).length;
    const rows = Math.max(0, lineCount - 1);

    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "providers.csv";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

    return { rows, bytes: blob.size };
}

export function csvFilenameFromRun(run, mode) {
    const cat = (run?.category || "category").toLowerCase().replace(/\s+/g, "-");
    const loc = (run?.location || "location").toLowerCase().replace(/\s+/g, "-");
    const suffix = !mode || mode === "standard" ? "" : `-${mode}`;
    return `providers-${cat}-${loc}${suffix}.csv`;
}
